import { Component, h, State, Method, Prop, Watch, Listen } from '@stencil/core';

export interface UiCmsFaqFeedbackBody {
  requestUrlFeedback: string;
  apiKey: string;
  apiVersion: string;
  uuid: string;
  faq: string;
  url: string;
}

interface HTMLDialogElement extends HTMLUiDialogElement {
  hide(): void;
}

@Component({
  tag: 'ui-cms-faq-feedback',
  styleUrl: 'ui-cms-faq-feedback.scss',
  shadow: true,
})
export class UiCmsFaqFeedback {
  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) body: UiCmsFaqFeedbackBody = {} as any;

  @Prop({ mutable: true }) i18n = {
    confirmCancelHeadline: 'Bearbeitung abbrechen?',
    confirmCancelText:
      'Möchten Sie die Bearbeitung des Formulars wirklich abbrechen? Ihre Daten werden nicht gespeichert.',
    confirmCancelButtonCancel: 'Abbrechen',
    confirmCancelButtonContinue: 'Bearbeitung fortsetzen',
    thankYouHeadline: 'Vielen Dank für Ihr Feedback!',
    thankYouText:
      'Danke, dass Sie Ihre Stimme abgegeben haben. Wir sind immer dabei unseren Service zu verbessern und freuen uns daher auf Ihre detaillierte Meinung.',
    feedbackPositiveQuestion: 'Was hat Ihnen gefallen',
    feedbackNegativeQuestion: 'Was hat Ihnen gefehlt',
    buttonCancel: 'Abbrechen',
    buttonConfirm: 'Feedback senden',
    feedbackError:
      'Ihre Nachricht konnte aus technischen Gründen nicht übermittelt werden. Bitte versuchen Sie es erneut oder nutzen Sie eine unserer anderen',
    contactOptions: 'Kontaktmöglichkeiten',
    feedbackErrorNetwork:
      'Ihr feedback konnte nicht übermittelt werden. Es besteht keine Verbindung zum Internet. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    feedbackPlaceholder: 'Bitte sagen Sie uns Ihre Meinung',
    feedbackSuccess: 'Ihr Feedback wurde erfolgreich übermittelt.',
    successInfo: 'Vielen Dank für Ihr Feedback. Wir kümmern uns umgehend darum.',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  private dialogElement: HTMLDialogElement;

  private textareaElement: HTMLTextAreaElement;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  @State() showConfirmationContent = false;

  @State() feedbackText = '';

  @State() isRatingPositive: boolean;

  @State() requestStatus: 'none' | 'pending' | 'success' | 'networkError' | 'generalError';

  @Watch('requestStatus')
  handleRequestStatusChange(newValue: 'none' | 'pending' | 'success' | 'networkError' | 'generalError') {
    if (newValue !== 'success') return;
    // Show confirmation for 3 seconds then close the modal
    setTimeout(() => this.closeFaqFeedbackModal(), 3000);
  }

  @Listen('sd-request-close')
  onRequestHide(e: CustomEvent) {
    e.preventDefault();
    this.onHideDialog();
  }

  @Listen('sd-initial-focus')
  onInitialFocus(e: CustomEvent) {
    e.preventDefault();
    this.textareaElement.focus();
    this.dialogElement.shadowRoot.querySelector('sd-icon').setAttribute('aria-label', 'Dialog schließen');
  }

  @Method()
  async showFaqFeebackModal(isRatingPositive: boolean): Promise<void> {
    this.isRatingPositive = isRatingPositive;
    await this.dialogElement.show();
  }

  private closeFaqFeedbackModal(): void {
    this.dialogElement.hide();
  }

  private async sendFeedback() {
    this.requestStatus = 'pending';

    const body = {
      uuid: this.body.uuid,
      url: this.body.url,
      isRatingPositive: this.isRatingPositive,
      faq: this.body.faq,
      feedback: this.feedbackText,
    };

    try {
      const res = await fetch(`${this.body.requestUrlFeedback}?api-version=${this.body.apiVersion}`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.body.apiKey,
        },
      });
      if (!res.ok) {
        throw new Error();
      }
      this.requestStatus = 'success';
    } catch (err) {
      if (navigator.onLine) {
        this.requestStatus = 'generalError';
        return;
      }
      this.requestStatus = 'networkError';
    }
  }

  private renderHeadline = (headline: string) => <h1 class="sd-headline sd-headline--size-3xl">{headline}</h1>;

  private renderParagraph = (text: string, style?: string) => <p class={`sd-paragraph ${style || ''}`}>{text}</p>;

  private renderAlert = (variant: 'danger' | 'success', text: string) => {
    const iconName = variant === 'danger' ? 'system/warning' : 'system/confirm-circle';
    return (
      <div
        class={{
          alert: true,
          'alert--danger': variant === 'danger',
          'alert--success': variant === 'success',
        }}
      >
        <div class="alert__message">
          <sd-icon library={window.ui.cmsState.iconLibrary} name={iconName} slot="icon-left"></sd-icon>
          <p class="sd-paragraph">{text}</p>
        </div>
      </div>
    );
  };

  private renderDialogFooter = () => (
    <div slot="footer">
      {(this.requestStatus === 'generalError' || this.requestStatus === 'networkError') && (
        <div class="sticky">
          {this.renderAlert(
            'danger',
            this.requestStatus === 'generalError' ? this.i18n.feedbackError : this.i18n.feedbackErrorNetwork
          )}
        </div>
      )}
      {this.requestStatus === 'success' && (
        <div class="sticky">{this.renderAlert('success', this.i18n.feedbackSuccess)}</div>
      )}
    </div>
  );

  private onHideDialog(): void {
    if (this.requestStatus === 'success') {
      this.showConfirmationContent = false;
      this.requestStatus = 'none';
      this.feedbackText = '';
      this.dialogElement.hide();
    }
    if (this.showConfirmationContent) {
      this.showConfirmationContent = false;
      this.feedbackText = '';
      this.dialogElement.hide();
    }
    this.showConfirmationContent = true;
  }

  render() {
    return (
      <sd-dialog
        ref={(el) => {
          this.dialogElement = el;
        }}
      >
        <div
          class={{
            'inner-container': true,
            hidden: !this.showConfirmationContent || this.requestStatus === 'success',
          }}
        >
          {this.renderHeadline(this.i18n.confirmCancelHeadline)}
          {this.renderParagraph(this.i18n.confirmCancelText)}
          <div class="buttons">
            <sd-button variant="secondary" onClick={() => this.closeFaqFeedbackModal()}>
              {this.i18n.confirmCancelButtonCancel}
            </sd-button>
            <sd-button
              variant="primary"
              onClick={() => {
                this.showConfirmationContent = false;
              }}
            >
              {this.i18n.confirmCancelButtonContinue}
            </sd-button>
          </div>
        </div>
        <div
          class={{
            hidden: this.requestStatus !== 'success',
          }}
        >
          <div class="inner-container">
            {this.renderHeadline(this.i18n.thankYouHeadline)}
            {this.renderParagraph(this.i18n.successInfo)}
          </div>
        </div>
        <div
          class={{
            'inner-container': true,
            hidden: this.showConfirmationContent || this.requestStatus === 'success',
          }}
        >
          {this.renderHeadline(this.i18n.thankYouHeadline)}
          {this.renderParagraph(this.i18n.thankYouText)}
          {this.renderParagraph(
            this.isRatingPositive ? this.i18n.feedbackPositiveQuestion : this.i18n.feedbackNegativeQuestion,
            'label'
          )}
          <sd-textarea
            size="lg"
            maxlength={1024}
            value={this.feedbackText}
            onInput={(e) => {
              this.feedbackText = e.target.value;
            }}
            ref={(el) => {
              this.textareaElement = el;
            }}
            placeholder={this.i18n.feedbackPlaceholder}
          ></sd-textarea>
          <div class="buttons">
            <sd-button variant="secondary" onClick={() => this.onHideDialog()}>
              {this.i18n.buttonCancel}
            </sd-button>
            <sd-button
              variant="primary"
              disabled={
                this.feedbackText.length > 1024 || this.requestStatus === 'pending' || this.feedbackText.length === 0
              }
              onClick={() => this.sendFeedback()}
            >
              <sd-icon library={window.ui.cmsState.iconLibrary} name="system/e-mail-send" slot="icon-left"></sd-icon>
              {this.i18n.buttonConfirm}
            </sd-button>
          </div>
        </div>
        {this.renderDialogFooter()}
      </sd-dialog>
    );
  }
}
